import axiosInstance from '@/setup/axios/axiosInstance'
import {UserModel, UserResponse, CreateUserRequest, UpdateUserRequest} from '../models/UserModel'
import {getCurrentCompanyId} from '@/modules/auth/helpers/CompanyStorage'

// Get all users with optional filters
export function getUsers(params?: {
  search?: string;
  status?: string[];
  role?: string[];
  page?: number;
  page_size?: number;
  ordering?: string;
  company?: string;
}) {
  const queryParams = new URLSearchParams();
  const companyId = params?.company || getCurrentCompanyId();
  
  if (params?.search) {
    queryParams.append('search', params.search);
  }
  
  if (params?.status && params.status.length > 0) {
    params.status.forEach(status => queryParams.append('status', status));
  }
  
  if (params?.role && params.role.length > 0) {
    params.role.forEach(role => queryParams.append('role', role));
  }

  if (params?.page) {
    queryParams.append('page', String(params.page));
  }

  if (params?.page_size) {
    queryParams.append('page_size', String(params.page_size));
  }
  
  if (params?.ordering) {
    queryParams.append('ordering', params.ordering);
  }

  if (companyId) {
    queryParams.append('company', companyId);
  }
  
  const url = `/accounts/users/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return axiosInstance.get<UserResponse>(url);
}

// Get single user by ID
export function getUser(id: string) {
  return axiosInstance.get<UserModel>(`/accounts/users/${id}/`)
}

// Create new user
export function createUser(userData: CreateUserRequest) {
  const company = userData.company || getCurrentCompanyId();
  return axiosInstance.post<UserModel>('/accounts/users/', {
    ...userData,
    company,
  })
    .catch((error) => {
      // Handle validation errors from backend
      if (error.response?.data?.type === 'validation_error') {
        throw new Error(JSON.stringify(error.response.data));
      }
      throw error;
    });
}

// Update user
export function updateUser(id: string, userData: UpdateUserRequest) {
  const company = userData.company || getCurrentCompanyId();
  return axiosInstance.patch<UserModel>(`/accounts/users/${id}/`, {
    ...userData,
    company,
  })
    .catch((error) => {
      // Handle validation errors from backend
      if (error.response?.data?.type === 'validation_error') {
        throw new Error(JSON.stringify(error.response.data));
      }
      throw error;
    });
}

// Delete user
export function deleteUser(id: string) {
  return axiosInstance.delete(`/accounts/users/${id}/`, {
    data: {
      company: getCurrentCompanyId(),
    },
  })
}

// Delete multiple users
export const deleteUsers = async (userIds: string[]): Promise<void> => {
  // Delete users one by one (backend doesn't support bulk delete)
  const company = getCurrentCompanyId();
  const deletePromises = userIds.map(userId => 
    axiosInstance.delete(`/accounts/users/${userId}/`, {
      data: {
        company,
      },
    })
  );
  
  await Promise.all(deletePromises);
}

// Get current user
export function getCurrentUser() {
  return axiosInstance.get<UserModel>('/accounts/users/me/')
}

// Get users by role
export function getUsersByRole(role: string) {
  return axiosInstance.get<UserResponse>(`/accounts/users/by_role/?role=${role}`)
}

// Get users by group
export function getUsersByGroup(group: string) {
  return axiosInstance.get<UserResponse>(`/accounts/users/by_group/?group=${group}`)
}

// Activate user
export function activateUser(id: string) {
  return axiosInstance.post(`/accounts/users/${id}/activate/`)
}

// Deactivate user
export function deactivateUser(id: string) {
  return axiosInstance.post(`/accounts/users/${id}/deactivate/`)
}

// Change password
export function changePassword(id: string, data: {
  old_password: string
  new_password: string
  confirm_password: string
}) {
  return axiosInstance.post(`/accounts/users/${id}/change_password/`, data)
}



